import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, Shield, Globe, Phone, Mail, Sparkles, Terminal, Layers, CheckCircle2, Activity } from "lucide-react";
import LookupWidget from "@/components/LookupWidget";
import VillainWall from "@/components/VillainWall";

const LOGOS = ["STRIPE", "CLOUDFLARE", "PLAID", "TWILIO", "OKTA", "SEGMENT", "RETOOL", "VERCEL"];

const FEATURES = [
  { icon: Phone, title: "Phone HLR & Carrier Lookup", desc: "Real-time HLR pings across 800+ mobile networks. Line portability, spam reputation, and DNC checks in under 300ms." },
  { icon: Mail, title: "Email Deliverability Intel", desc: "Syntax + MX + SMTP handshake in one call. Detects disposable, role, catch-all and free providers with confidence scoring." },
  { icon: Globe, title: "IP Threat Intelligence", desc: "Geolocate any IPv4 or IPv6, then flag Tor exits, residential proxies, commercial VPNs and datacenter ranges instantly." },
  { icon: Sparkles, title: "AI Agent Connector (MCP)", desc: "Native Model Context Protocol server with OAuth 2.1 — plug The Good Guys into Claude, ChatGPT & LangGraph without keys." },
  { icon: Shield, title: "Fraud Signal Aggregation", desc: "Cross-referenced feeds from carrier systems, blocklists, and global threat databases update every 60 seconds." },
  { icon: Layers, title: "Bulk CSV & Streaming", desc: "Drop a CSV of 100k rows and stream verified output. Or hit us at 200 QPS from your own pipeline." },
];

const STATS = [
  { label: "Median Latency", value: "187ms", sub: "P50 across 12 regions" },
  { label: "Networks Covered", value: "812", sub: "Global HLR carriers" },
  { label: "IPs Classified", value: "4.1B", sub: "Refreshed hourly" },
  { label: "Signal Sources", value: "40+", sub: "Threat + carrier feeds" },
];

const TESTIMONIALS = [
  {
    quote: "We cut signup fraud 68% in six weeks. The HLR + IP risk combo is the cleanest fraud signal we've integrated in ten years of ops.",
    name: "Elena Rostova",
    title: "Head of Fraud Engineering, FinPay",
    img: "https://images.pexels.com/photos/19805878/pexels-photo-19805878.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    quote: "Their MCP connector was live in our AI agents in under an hour. The latency budget for enrichment used to be our bottleneck — no longer.",
    name: "Marcus Vance",
    title: "VP Security Operations, DataSphere",
    img: "https://images.unsplash.com/photo-1649123245135-4db6ead931b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHx0ZWNoJTIwZGV2ZWxvcGVyJTIwZXhlY3V0aXZlJTIwcG9ydHJhaXQlMjBhdmF0YXJ8ZW58MHx8fHwxNzg5ODYyMjg4fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    quote: "One credit, three verification types, zero surprises. The rollover policy actually respects developers — that alone is refreshing.",
    name: "Arjun Patel",
    title: "Lead API Architect, CloudScale",
    img: "https://images.pexels.com/photos/35140700/pexels-photo-35140700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function Landing() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-70 pointer-events-none" />
        <div className="absolute top-40 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-8 reveal">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-signal">On duty · 187ms p50 · 99.99% SLA</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="reveal">
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
                We're the good guys.
                <br />
                <span className="text-cyan-signal">Meet the bad ones.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-400 max-w-xl leading-relaxed">
                One API to spot spam callers, disposable inboxes, Tor exits and VPN proxies before they touch your users. Phone HLR, email deliverability and IP threat intel — all in under 300ms.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/register" data-testid="hero-cta-signup">
                  <Button size="lg" className="bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold h-12 px-6">
                    Start free · 500 credits <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/docs" data-testid="hero-cta-docs">
                  <Button size="lg" variant="outline" className="border-slate-700 bg-slate-900/50 text-slate-100 hover:bg-slate-800 h-12 px-6">
                    <Terminal className="w-4 h-4 mr-2" /> Read the docs
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono text-slate-500">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> No credit card required</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Credits roll over 3 months</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> MCP-native for AI agents</span>
              </div>
            </div>

            <div className="reveal">
              <div className="mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4 text-cyan-signal" />
                <span className="font-mono text-xs uppercase tracking-widest text-slate-400">Live sandbox — try any input</span>
              </div>
              <LookupWidget authed={false} defaultTab="phone" />
            </div>
          </div>

          {/* Logos strip */}
          <div className="mt-20 border-y border-slate-800 py-6 overflow-hidden">
            <div className="flex items-center gap-16 marquee whitespace-nowrap">
              {[...LOGOS, ...LOGOS, ...LOGOS].map((l, i) => (
                <span key={i} className="font-display font-bold text-slate-600 text-xl tracking-[0.2em]">{l}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
              <div className="text-3xl sm:text-4xl font-display font-bold text-cyan-signal tracking-tight">{s.value}</div>
              <div className="mt-2 text-xs font-mono uppercase tracking-widest text-slate-500">{s.label}</div>
              <div className="mt-1 text-xs text-slate-500">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Villain wall */}
      <VillainWall />

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-2xl mb-12">
          <span className="font-mono text-xs uppercase tracking-widest text-cyan-signal">Capabilities</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">
            One credit. Any verification. Zero surprise bills.
          </h2>
          <p className="mt-4 text-slate-400">Every phone, email, or IP lookup consumes exactly 1 credit. Bulk it, stream it, or plug it into your AI agent — the pricing is the same across the board.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="group rounded-xl border border-slate-800 bg-slate-900/40 p-6 hover-lift hover:border-cyan-500/30">
              <div className="w-10 h-10 rounded-lg border border-slate-800 bg-slate-950 flex items-center justify-center mb-4 group-hover:border-cyan-500/40 group-hover:bg-cyan-500/5 transition-colors">
                <f.icon className="w-5 h-5 text-cyan-signal" />
              </div>
              <h3 className="font-display font-semibold text-lg">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Code sample */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="font-mono text-xs uppercase tracking-widest text-cyan-signal">For developers</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold tracking-tight">A REST API you actually want to use.</h2>
            <p className="mt-4 text-slate-400">HTTPS. Bearer tokens. JSON in, JSON out. No SDK required — but we ship idiomatic ones for JS, Python, Go and Ruby.</p>
            <ul className="mt-6 space-y-3">
              {[
                "One universal endpoint per validator",
                "Deterministic response schema — never a breaking change",
                "MCP server for Claude, ChatGPT, LangGraph agents",
                "Rate-limited free tier plus 25 browser tools",
              ].map((t) => (
                <li key={t} className="flex gap-3 text-sm text-slate-300">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <Link to="/docs"><Button variant="outline" className="border-slate-700 bg-slate-900 hover:bg-slate-800">Read the docs →</Button></Link>
            </div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-950 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800 bg-slate-900/60">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-rose-500/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
              </div>
              <span className="font-mono text-xs text-slate-400 ml-2">curl · phone HLR lookup</span>
            </div>
            <pre className="p-5 text-xs sm:text-sm font-mono text-cyan-200 leading-relaxed overflow-x-auto">
{`$ curl https://api.thegoodguys.app/api/validate/phone \\
    -H "Authorization: Bearer gg_live_9J2..." \\
    -H "Content-Type: application/json" \\
    -d '{"value":"+14155552671"}'

{
  "kind": "phone",
  "credits_used": 1,
  "result": {
    "e164":       "+14155552671",
    "carrier":    "Verizon Wireless",
    "line_type":  "mobile",
    "country":    "United States",
    "ported":     false,
    "spam_risk":  "low",
    "hlr_status": "active"
  }
}`}
            </pre>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-2xl mb-10">
          <span className="font-mono text-xs uppercase tracking-widest text-cyan-signal">Trusted at scale</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold tracking-tight">Fraud teams shipping with The Good Guys.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 hover-lift hover:border-cyan-500/20">
              <p className="text-slate-300 leading-relaxed">"{t.quote}"</p>
              <div className="mt-6 flex items-center gap-3">
                <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover border border-slate-800" />
                <div>
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.title}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950/30 p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
          <div className="relative">
            <Zap className="w-10 h-10 text-cyan-signal mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight max-w-2xl mx-auto">
              Ship trust signals in the next hour.
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">Register for free, generate a Bearer token, and hit any of our validators with your first request. 500 credits are on us.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register" data-testid="cta-bottom-signup"><Button size="lg" className="bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold">Get your API key →</Button></Link>
              <Link to="/pricing" data-testid="cta-bottom-pricing"><Button size="lg" variant="outline" className="border-slate-700 bg-slate-900/50 hover:bg-slate-800">See pricing</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
